import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, CheckCircle2, Clock, AlertCircle, Info, RefreshCw, ChevronRight, WifiOff } from "lucide-react";
import { ApiService } from "../../services/api";
import TeachFlow from "./TeachFlow";
import MakeupRunner from "./MakeupRunner";
import type { CoachPlanResponse, PlanItem, PlanItemYks } from "../../types/navigator";
import { coachErrorMessage, useCoachAutoRefresh } from "./coachLive";

// "Planım" — koçun kurduğu takvim. Kalemler buradan da BAŞLATILABİLİR (sahibin isteği:
// "planım oluştuktan sonra derslerin üzerine tıklayamıyorum"). Bugün ekranıyla aynı
// motorlara gider: TEST → E-Eğitim test modu, TEACH → AI öğretmen, MAKEUP → telafi seti.

const TYPE_LABEL: Record<string, string> = {
  TEST: "Test",
  TEACH: "Konu",
  MAKEUP: "Telafi",
  REVIEW: "Tekrar",
  BOOK: "Kitap",
};

// Yol haritası etiketi — kalem metadata.yks ile geldiyse
const YKS_CHIP: Record<string, { label: string; cls: string }> = {
  ONCELIKLI: { label: "Öncelikli", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  SONRA: { label: "Sonra", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  ATLANABILIR: { label: "Atlanabilir", cls: "border-gray-200 bg-gray-50 text-gray-500" },
};

const STATUS_CHIP: Record<string, string> = {
  DONE: "border-emerald-200 bg-emerald-50 text-emerald-700",
  MISSED: "border-amber-200 bg-amber-50 text-amber-700",
  SKIPPED: "border-gray-200 bg-gray-50 text-gray-500",
  PENDING: "border-gray-200 bg-white text-gray-600",
};

// Verilen ödevin teslim günü ("26 Eki")
const fmtDeadline = (d: string) => new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
const dayFmt = new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "long", weekday: "long" });

const fmtDay = (iso: string) => {
  try {
    return dayFmt.format(new Date(`${String(iso).slice(0, 10)}T12:00:00`));
  } catch {
    return iso;
  }
};

export default function CoachPlan() {
  const navigate = useNavigate();
  const [data, setData] = useState<CoachPlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revising, setRevising] = useState(false);
  const [teach, setTeach] = useState<{ gainId?: string; sectionId?: string } | null>(null);
  const [makeupItemId, setMakeupItemId] = useState<string | null>(null);

  // Kalemi başlat — Bugün ekranındaki start() ile birebir aynı yönlendirme.
  const start = (item: PlanItem) => {
    const a = item.action;
    if (!a) return;
    if (a.kind === "exam" && a.contentId) {
      navigate(`/lessons/play/${a.lessonId}?contentId=${a.contentId}`);
      return;
    }
    if (a.kind === "video" && a.lessonId) {
      navigate(`/lessons/play/${a.lessonId}${a.contentId ? `?contentId=${a.contentId}` : ""}`);
      return;
    }
    if (a.kind === "book") {
      navigate(`/navigator?tab=kitaplar${a.bookId ? `&kitap=${encodeURIComponent(a.bookId)}` : ""}${a.testId ? `&test=${encodeURIComponent(a.testId)}` : ""}`);
      return;
    }
    if (a.kind === "teach") {
      setTeach({ gainId: a.gainId || undefined, sectionId: a.sectionId || undefined });
      return;
    }
    if (a.kind === "makeup") {
      setMakeupItemId(a.itemId);
    }
  };

  const loadSeq = useRef(0);

  // Hata, plan ekrandayken listeyi SİLMEZ — üstte şerit olarak görünür, bir sonraki başarılı yüklemede kalkar.
  const load = useCallback(async (silent = false) => {
    const seq = ++loadSeq.current; // üst üste yenilemede geç dönen eski yanıt yenisini ezmesin
    try {
      if (!silent) setLoading(true);
      const res = await ApiService.getCoachPlan();
      if (seq !== loadSeq.current) return;
      setData(res);
      setError(null);
    } catch (e: any) {
      if (seq === loadSeq.current) setError(coachErrorMessage(e, "Plan yüklenemedi"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);
  useCoachAutoRefresh(refresh);

  const byDay = useMemo(() => {
    const map = new Map<string, PlanItem[]>();
    for (const item of data?.items || []) {
      const day = String(item.scheduledDate).slice(0, 10);
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(item);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  const revise = async () => {
    setRevising(true);
    try {
      await ApiService.createCoachPlan();
      await load(true);
    } catch (e: any) {
      setError(coachErrorMessage(e, "Plan yenilenemedi"));
    } finally {
      setRevising(false);
    }
  };

  if (loading) {
    return (
      <div className="grid min-h-[40vh] place-items-center">
        <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data && error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-5">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span className="min-w-0 flex-1">{error}</span>
          <button
            onClick={() => load()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Tekrar dene
          </button>
        </div>
      </div>
    );
  }

  const plan = data?.plan;
  const stats = plan?.stats || {};

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-5 sm:px-6">
      {/* Yenileme düştü (çoğunlukla bağlantı) — plan yerinde kalır; internet gelince kendiliğinden tazelenir */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
          <WifiOff className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">{error}</span>
          <button
            onClick={refresh}
            className="shrink-0 rounded-md border border-amber-200 bg-white px-2.5 py-1 font-semibold transition-colors hover:bg-amber-100"
          >
            Tekrar dene
          </button>
        </div>
      )}

      {plan && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <CalendarDays className="h-5 w-5 text-blue-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">
                {plan.startDate} → {plan.endDate}
              </p>
              <p className="text-xs text-gray-500">
                {stats.totalItems ?? 0} iş · {stats.dayCount ?? 0} gün · {stats.testCount ?? 0} test ·{" "}
                {stats.teachCount ?? 0} konu · v{plan.version}
              </p>
            </div>
            <button
              onClick={revise}
              disabled={revising}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${revising ? "animate-spin" : ""}`} />
              Planı yenile
            </button>
          </div>

          {/* Koç kararını açıklıyor — plan neden böyle kuruldu */}
          {plan.rationale?.note && (
            <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
              {plan.rationale.note}
              {plan.rationale.skippedCompletedTests > 0 && (
                <span> Daha önce çözdüğün {plan.rationale.skippedCompletedTests} test plana konmadı.</span>
              )}
            </p>
          )}
        </div>
      )}

      {byDay.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-600">
          Planında henüz iş yok.
        </div>
      )}

      {byDay.map(([day, items]) => {
        const minutes = items.reduce((a, i) => a + i.estimatedMinutes, 0);
        return (
          <div key={day}>
            <div className="mb-2 flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">{fmtDay(day)}</h3>
              <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                <Clock className="h-3 w-3" /> {minutes} dk
              </span>
            </div>
            <div className="space-y-1.5">
              {items.map((item) => {
                const actionable = item.status === "PENDING" || item.status === "MISSED";
                // Bitmiş konu/eksik kalemine yeniden girilebilir (Bugün ekranıyla aynı kural)
                const reopenable = item.status === "DONE" && (item.type === "TEACH" || item.type === "REVIEW");
                const clickable = actionable || reopenable;
                const yks = (item.metadata?.yks as PlanItemYks | undefined) || null;
                const yksChip = yks ? YKS_CHIP[yks.etiket] : null;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => clickable && start(item)}
                    disabled={!clickable}
                    title={reopenable ? "Tekrar çalış" : undefined}
                    className={`flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left ${
                      clickable ? "cursor-pointer transition-colors hover:border-blue-200 hover:bg-blue-50/50" : "cursor-default"
                    }`}
                  >
                    {item.status === "DONE" ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                    ) : (
                      <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-200" />
                    )}
                    <span
                      className={`min-w-0 flex-1 truncate text-sm ${
                        item.status === "DONE" ? "text-gray-400 line-through" : "text-gray-800"
                      }`}
                    >
                      {item.title}
                    </span>
                    <span className="shrink-0 text-[11px] text-gray-400">{item.action?.kind === "video" ? "Video" : TYPE_LABEL[item.type] || item.type}</span>
                    {item.metadata?.origin === "homework" && (
                      <span
                        title="Öğretmeninin verdiği ödev — koç teslimine yetişecek güne yerleştirdi"
                        className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700"
                      >
                        Ödev{item.metadata?.deadline ? ` · teslim ${fmtDeadline(String(item.metadata.deadline))}` : ""}
                      </span>
                    )}
                    {yks && yksChip && (
                      <span
                        title={`Yol haritası sırası ${yks.sira}`}
                        className={`shrink-0 rounded-full border px-1.5 py-px text-[10px] font-medium ${yksChip.cls}`}
                      >
                        {yksChip.label}
                      </span>
                    )}
                    <span
                      className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                        STATUS_CHIP[item.status] || STATUS_CHIP.PENDING
                      }`}
                    >
                      {item.status === "DONE"
                        ? item.result?.percent !== undefined
                          ? `%${item.result.percent}`
                          : "bitti"
                        : item.status === "MISSED"
                        ? "geçti"
                        : item.status === "SKIPPED"
                        ? "atlandı"
                        : `${item.estimatedMinutes} dk`}
                    </span>
                    {clickable && <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {teach && (
        <TeachFlow
          gainId={teach.gainId}
          sectionId={teach.sectionId}
          onClose={() => {
            setTeach(null);
            load(true);
          }}
        />
      )}

      {makeupItemId && (
        <MakeupRunner
          itemId={makeupItemId}
          onClose={() => {
            setMakeupItemId(null);
            load(true);
          }}
          onCompleted={() => load(true)}
        />
      )}
    </div>
  );
}
